import { useState } from 'react';
import { useActiveFleet } from '@/data/fleets';
import { useFleetStats, type StatsRange } from '@/data/stats';
import {
  DateRangePicker,
  presetRange,
  type RangePreset,
} from '@/components/date-range-picker';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { TableScroll } from '@/components/ui/table-scroll';
import { formatMoney } from '@/lib/format';
import { vehicleTypeLabel } from '@/lib/vehicle-labels';
import { cn } from '@/lib/cn';
import type {
  FleetStats,
  StatsCustomerDebt,
  StatsMonthBucket,
  StatsWeekBucket,
} from '@breaker/shared-types';

/**
 * Money Suite M1 — fleet Statistics. Read-only; every number comes from the
 * single /stats aggregate endpoint. Charts are single-series bars in neutral
 * ink (red stays reserved for actions and the concentration callout), with
 * the tables alongside acting as the accessible data view.
 *
 * Named gap (M3): margin per job is NOT rendered — driver pay isn't recorded
 * anywhere yet, and a proxy would be invented data.
 */

const PLACEHOLDER = '—';

function formatPct(pct: number | null | undefined, digits = 0): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return PLACEHOLDER;
  return `${pct.toFixed(digits)}%`;
}

function jobsLabel(n: number): string {
  return `${n} ${n === 1 ? 'job' : 'jobs'}`;
}

/** Whole pounds for chart direct labels — pence noise doesn't fit on a bar. */
function formatMoneyCompact(pence: number): string {
  return `£${Math.round(pence / 100).toLocaleString('en-GB')}`;
}

const MONTH_FMT = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });
const DAY_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** 'YYYY-MM' → 'Sep' ('Jan 26' at year boundaries so the axis stays anchored). */
function monthLabel(month: string, withYear: boolean): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  const m = MONTH_FMT.format(d);
  return withYear ? `${m} ${String(d.getUTCFullYear()).slice(2)}` : m;
}

/** The fixed trailing-12-months axis, zero-filled (mirrors the server window). */
function fillTrendMonths(trend: StatsMonthBucket[]): StatsMonthBucket[] {
  const byMonth = new Map(trend.map((b) => [b.month, b]));
  const now = new Date();
  const out: StatsMonthBucket[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    out.push(byMonth.get(key) ?? { month: key, revenueNetPence: 0, jobs: 0 });
  }
  return out;
}

/** UTC Monday of the week containing `ms`, as 'YYYY-MM-DD' (matches date_trunc('week')). */
function utcMonday(ms: number): string {
  const d = new Date(ms);
  const dayOffset = (d.getUTCDay() + 6) % 7;
  const monday = new Date(ms - dayOffset * 86_400_000);
  return monday.toISOString().slice(0, 10);
}

function fillWeeks(weeks: StatsWeekBucket[], range: StatsRange): StatsWeekBucket[] {
  const byWeek = new Map(weeks.map((b) => [b.weekStart, b]));
  const out: StatsWeekBucket[] = [];
  const endKey = utcMonday(new Date(range.to).getTime());
  // Cap the axis: beyond ~30 bars a weekly chart stops being readable.
  for (
    let cursor = new Date(`${utcMonday(new Date(range.from).getTime())}T00:00:00Z`).getTime();
    out.length < 30;
    cursor += 7 * 86_400_000
  ) {
    const key = utcMonday(cursor);
    out.push(byWeek.get(key) ?? { weekStart: key, jobs: 0 });
    if (key === endKey) break;
  }
  return out;
}

// -------- chart + tile primitives --------

function StatTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="surface rounded-lg px-4 py-3">
      <div className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2">
        {label}
      </div>
      <div className="text-xl font-semibold tracking-tight text-breaker-ink mt-1">
        {value}
      </div>
      {sub ? <div className="text-xs text-breaker-mute mt-0.5">{sub}</div> : null}
    </div>
  );
}

interface ColumnDatum {
  key: string;
  axisLabel: string | null;
  value: number;
  /** Native tooltip: full period + value. */
  title: string;
  /** Selective direct label (max + latest bars only). */
  directLabel?: string;
}

/**
 * Single-series column chart: neutral-ink thin bars, 4px rounded data-ends
 * anchored to the baseline, 2px gaps, per-bar native tooltip. No legend —
 * the card title names the series.
 */
function ColumnChart({ data, ariaLabel }: { data: ColumnDatum[]; ariaLabel: string }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div role="img" aria-label={ariaLabel}>
      <div className="flex items-end gap-[2px] h-28">
        {data.map((d) => (
          <div
            key={d.key}
            className="flex-1 min-w-0 h-full flex flex-col justify-end items-center"
            title={d.title}
          >
            {d.directLabel ? (
              <div className="text-2xs text-breaker-ink-2 mb-0.5 whitespace-nowrap">
                {d.directLabel}
              </div>
            ) : null}
            <div
              className={cn(
                'w-full max-w-[28px] rounded-t bg-breaker-ink-2',
                d.value === 0 && 'h-[2px] bg-breaker-line-2',
              )}
              style={d.value > 0 ? { height: `${(d.value / max) * 100}%` } : undefined}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-[2px] mt-1 border-t border-breaker-line pt-1">
        {data.map((d) => (
          <div
            key={d.key}
            className="flex-1 min-w-0 text-center text-2xs text-breaker-mute whitespace-nowrap overflow-visible"
          >
            {d.axisLabel ?? ''}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Horizontal share bar on a quiet track — magnitude, single hue. */
function ShareBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 rounded-full bg-breaker-ground-2 border border-breaker-line overflow-hidden min-w-[64px]">
      <div
        className="h-full rounded-full bg-breaker-ink-2"
        style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
      />
    </div>
  );
}

function StatRow({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-breaker-line last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm text-breaker-ink">{label}</div>
        <div className="text-xs text-breaker-mute mt-0.5">{caption}</div>
      </div>
      <div className="text-base font-semibold tracking-tight text-breaker-ink whitespace-nowrap">
        {value}
      </div>
    </div>
  );
}

// -------- sections --------

function RevenueTrendCard({ stats }: { stats: FleetStats }) {
  const months = fillTrendMonths(stats.trend);
  const max = Math.max(...months.map((m) => m.revenueNetPence));
  const data: ColumnDatum[] = months.map((m, i) => {
    const isYearAnchor = i === 0 || m.month.endsWith('-01');
    const isMax = max > 0 && m.revenueNetPence === max;
    const isLatest = i === months.length - 1;
    // Quarterly ticks (plus the latest month) — twelve labels collide on mobile.
    const showTick = i % 3 === 0 || isLatest;
    return {
      key: m.month,
      axisLabel: showTick ? monthLabel(m.month, isYearAnchor) : null,
      value: m.revenueNetPence,
      title: `${monthLabel(m.month, true)}: ${formatMoney(m.revenueNetPence)} · ${jobsLabel(m.jobs)}`,
      ...(isMax || (isLatest && m.revenueNetPence > 0)
        ? { directLabel: formatMoneyCompact(m.revenueNetPence) }
        : {}),
    };
  });
  return (
    <Card title="Revenue — last 12 months (ex-VAT), from job pricing">
      <ColumnChart data={data} ariaLabel="Monthly revenue, trailing twelve months" />
      <p className="text-2xs text-breaker-mute mt-2">
        {/* The trend stays on job pricing even when the headline is on
            invoices: it is a fixed 12-month window that usually reaches back
            before the fleet's first invoice, and a series that silently
            switched basis part-way along would show a cliff that looks like
            lost work. Saying which basis it uses costs a few words; a
            mysterious cliff costs a support conversation. */}
        Priced from the jobs themselves, so the series is continuous across the
        month you started invoicing in Breaker. Fixed 12-month window,
        independent of the range picker. Months bucket in UTC.
      </p>
    </Card>
  );
}

function RevenueByCustomerCard({ stats }: { stats: FleetStats }) {
  const rows = stats.revenueByCustomer;
  const top = rows.find((r) => r.customerId !== null) ?? rows[0];
  return (
    <Card title="Revenue by customer">
      {top && top.sharePct > 0 ? (
        <p className="text-sm text-breaker-ink bg-breaker-amber/10 rounded-md px-3 py-2 mb-3">
          <span className="font-semibold">{top.customerName ?? 'Unassigned work'}</span>{' '}
          is {formatPct(top.sharePct)} of revenue in this period.
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-breaker-mute">No completed jobs in this period.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-2xs uppercase tracking-wider text-breaker-ink-2">
                <th className="py-1.5 pr-4 font-medium">Customer</th>
                <th className="py-1.5 pr-4 font-medium text-right">Jobs</th>
                <th className="py-1.5 pr-4 font-medium text-right">Revenue</th>
                <th className="py-1.5 pr-4 font-medium text-right">Share</th>
                <th className="py-1.5 font-medium w-[30%]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.customerId ?? 'none'} className="border-t border-breaker-line">
                  <td className="py-2 pr-4 text-breaker-ink">
                    {r.customerName ?? <span className="text-breaker-mute">No customer</span>}
                  </td>
                  <td className="py-2 pr-4 text-right text-breaker-ink-2">{r.jobs}</td>
                  <td className="py-2 pr-4 text-right text-breaker-ink">
                    {formatMoney(r.revenueNetPence)}
                  </td>
                  <td className="py-2 pr-4 text-right text-breaker-ink-2">
                    {formatPct(r.sharePct)}
                  </td>
                  <td className="py-2">
                    <ShareBar pct={r.sharePct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Card>
  );
}

function BreakdownCard({
  title,
  rows,
  emptyText,
}: {
  title: string;
  rows: Array<{ key: string; label: string; jobs: number; revenueNetPence: number }>;
  emptyText: string;
}) {
  const max = Math.max(...rows.map((r) => r.revenueNetPence), 1);
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-breaker-mute">{emptyText}</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-breaker-ink truncate">{r.label}</span>
                <span className="text-breaker-ink whitespace-nowrap">
                  {formatMoney(r.revenueNetPence)}
                  <span className="text-breaker-mute text-xs"> · {jobsLabel(r.jobs)}</span>
                </span>
              </div>
              <div className="mt-1">
                <ShareBar pct={(r.revenueNetPence / max) * 100} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function OperationsCard({ stats, range }: { stats: FleetStats; range: StatsRange }) {
  const { operations } = stats;
  const weeks = fillWeeks(operations.jobsPerWeek, range);
  const maxJobs = Math.max(...weeks.map((w) => w.jobs));
  const weekData: ColumnDatum[] = weeks.map((w, i) => {
    const d = new Date(`${w.weekStart}T00:00:00Z`);
    // Label roughly every 4th tick so the axis breathes on mobile.
    const showLabel = weeks.length <= 8 || i % 4 === 0 || i === weeks.length - 1;
    return {
      key: w.weekStart,
      axisLabel: showLabel ? DAY_FMT.format(d) : null,
      value: w.jobs,
      title: `Week of ${DAY_FMT.format(d)}: ${jobsLabel(w.jobs)}`,
      ...(maxJobs > 0 && w.jobs === maxJobs ? { directLabel: String(w.jobs) } : {}),
    };
  });
  return (
    <Card title="Operations">
      <div className="mb-4">
        <div className="text-xs text-breaker-mute mb-2">Completed jobs per week</div>
        <ColumnChart data={weekData} ariaLabel="Completed jobs per week" />
      </div>
      <StatRow
        label="On-time deliveries"
        value={formatPct(operations.onTime.pct)}
        caption={
          operations.onTime.measured > 0
            ? `${operations.onTime.onTime} of ${operations.onTime.measured} windowed drops on time — ${operations.onTime.measured} of ${operations.onTime.totalDelivered} drops carried a time window`
            : 'No delivered stops carried a time window in this period'
        }
      />
      <StatRow
        label="POD compliance"
        value={formatPct(operations.pod.pct)}
        caption={
          operations.pod.completedLoads > 0
            ? `${operations.pod.withPod} of ${operations.pod.completedLoads} completed jobs have a captured POD`
            : 'No completed jobs in this period'
        }
      />
      <StatRow
        label="Cancellation rate"
        value={formatPct(operations.cancellation.pct)}
        caption={
          operations.cancellation.totalFinished > 0
            ? `${operations.cancellation.cancelled} cancelled of ${operations.cancellation.totalFinished} finished (completed, failed or cancelled)`
            : 'Nothing finished in this period'
        }
      />
    </Card>
  );
}

function debtCell(pence: number): string {
  return pence > 0 ? formatMoney(pence) : PLACEHOLDER;
}

function paysLabel(row: StatsCustomerDebt): string {
  if (row.avgDaysLate === null) return PLACEHOLDER;
  const days = Math.round(Math.abs(row.avgDaysLate));
  if (days === 0) return 'on time';
  return row.avgDaysLate > 0 ? `${days}d late` : `${days}d early`;
}

function MoneyHygieneCard({ stats }: { stats: FleetStats }) {
  const { hasInvoices, customers } = stats.moneyHygiene;
  return (
    <Card title="Money hygiene — invoices">
      {!hasInvoices ? (
        <p className="text-sm text-breaker-mute">
          No invoices issued in Breaker yet — this section lights up once invoicing
          lands here (Money Suite M2).
        </p>
      ) : (
        <>
          <TableScroll>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-2xs uppercase tracking-wider text-breaker-ink-2">
                  <th className="py-1.5 pr-4 font-medium">Customer</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Current</th>
                  <th className="py-1.5 pr-4 font-medium text-right">1–30d</th>
                  <th className="py-1.5 pr-4 font-medium text-right">31–60d</th>
                  <th className="py-1.5 pr-4 font-medium text-right">61d+</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Owed</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Days to pay*</th>
                  <th className="py-1.5 font-medium text-right">Pays*</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.customerId} className="border-t border-breaker-line">
                    <td className="py-2 pr-4 text-breaker-ink">{c.customerName}</td>
                    <td className="py-2 pr-4 text-right text-breaker-ink-2">
                      {debtCell(c.currentPence)}
                    </td>
                    <td className="py-2 pr-4 text-right text-breaker-ink-2">
                      {debtCell(c.overdue1To30Pence)}
                    </td>
                    <td className="py-2 pr-4 text-right text-breaker-ink-2">
                      {debtCell(c.overdue31To60Pence)}
                    </td>
                    <td
                      className={cn(
                        'py-2 pr-4 text-right',
                        c.overdue61PlusPence > 0
                          ? 'text-breaker-red font-medium'
                          : 'text-breaker-ink-2',
                      )}
                    >
                      {debtCell(c.overdue61PlusPence)}
                    </td>
                    <td className="py-2 pr-4 text-right font-medium text-breaker-ink">
                      {debtCell(c.outstandingPence)}
                    </td>
                    <td className="py-2 pr-4 text-right text-breaker-ink-2">
                      {c.avgDaysToPay === null
                        ? PLACEHOLDER
                        : `${Math.round(c.avgDaysToPay)}d`}
                    </td>
                    <td className="py-2 text-right text-breaker-ink-2">{paysLabel(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <p className="text-2xs text-breaker-mute mt-2">
            Ageing is against the invoice due date (or issue date + the customer's
            payment terms when none was set). *Days to pay is approximate — measured
            from when the invoice was marked paid in Breaker, across all paid
            invoices.
          </p>
        </>
      )}
    </Card>
  );
}

// -------- page --------

export function StatisticsPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const [preset, setPreset] = useState<RangePreset>('this_month');
  const [range, setRange] = useState<StatsRange | null>(() => presetRange('this_month'));

  const statsQuery = useFleetStats(fleetId, range);
  const stats = statsQuery.data;

  return (
    <div className="px-8 py-10 max-w-[1100px] max-md:px-4">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Statistics
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Revenue, operations and money hygiene for this fleet.
          </p>
        </div>
        <DateRangePicker
          preset={preset}
          onChange={(p, r) => {
            setPreset(p);
            if (r) setRange(r);
          }}
        />
      </header>

      {statsQuery.isError ? (
        <EmptyState
          title="Could not load statistics"
          description={(statsQuery.error as Error)?.message ?? 'Server error.'}
        />
      ) : !stats ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile
              /* The basis is NAMED, not hinted at: "revenue" means two
                 different things before and after a fleet starts invoicing in
                 Breaker, and a figure whose meaning changed silently is worse
                 than either meaning. Decided per range, so a month before the
                 fleet's first invoice still reports its real load-priced
                 number rather than a truthful-looking zero. */
              label={
                stats.revenueBasis === 'invoices'
                  ? 'Revenue invoiced (ex-VAT)'
                  : 'Revenue from job pricing (ex-VAT)'
              }
              value={formatMoney(stats.headline.revenueNetPence)}
              sub={
                stats.revenueBasis === 'invoices'
                  ? `+ ${formatMoney(stats.headline.revenueVatPence)} VAT · net of credit notes`
                  : `+ ${formatMoney(stats.headline.revenueVatPence)} VAT · not yet invoiced`
              }
            />
            <StatTile
              label="Jobs completed"
              value={String(stats.headline.jobsCompleted)}
            />
            <StatTile
              label="On-time"
              value={formatPct(stats.headline.onTimePct)}
              sub={
                stats.operations.onTime.measured > 0
                  ? `${stats.operations.onTime.measured} windowed drops`
                  : 'no windowed drops'
              }
            />
            <StatTile
              label="Outstanding"
              value={formatMoney(stats.headline.outstandingGrossPence)}
              sub="issued, unpaid invoices"
            />
          </div>

          <RevenueTrendCard stats={stats} />
          <RevenueByCustomerCard stats={stats} />

          <div className="grid md:grid-cols-2 gap-5">
            <BreakdownCard
              title="Revenue by vehicle class"
              emptyText="No completed jobs in this period."
              rows={stats.revenueByVehicleClass.map((r) => ({
                key: r.vehicleType ?? 'none',
                label: r.vehicleType ? vehicleTypeLabel(r.vehicleType) : 'Unspecified',
                jobs: r.jobs,
                revenueNetPence: r.revenueNetPence,
              }))}
            />
            <BreakdownCard
              title="Revenue by driver"
              emptyText="No completed jobs in this period."
              rows={stats.revenueByDriver.map((r) => ({
                key: r.driverUserId ?? 'none',
                label: r.driverName ?? 'Unassigned',
                jobs: r.jobs,
                revenueNetPence: r.revenueNetPence,
              }))}
            />
          </div>

          {range ? <OperationsCard stats={stats} range={range} /> : null}
          <MoneyHygieneCard stats={stats} />
        </div>
      )}
    </div>
  );
}
